(function () {
    'use strict';

    function initArchiveFilter(filter) {
        var parameter = filter.dataset.filterParam;
        var resultsSelector = filter.dataset.filterResults;
        var results = resultsSelector
            ? document.querySelector(resultsSelector)
            : null;
        var buttons = Array.prototype.slice.call(
            filter.querySelectorAll('[data-filter-value]')
        );
        var indicator = filter.querySelector('.archive-filter__indicator');
        var requestController = null;
        var requestNumber = 0;
        var isLoading = false;

        if (!parameter || !buttons.length) {
            return;
        }

        filter.classList.add('is-enhanced');

        function moveIndicator(activeButton, shouldAnimate) {
            var targetWidth = activeButton.offsetWidth;
            var targetX = activeButton.offsetLeft;
            var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            var canUseGsap = indicator && typeof window.gsap !== 'undefined';

            filter.style.setProperty('--indicator-width', targetWidth);
            filter.style.setProperty('--indicator-x', targetX);

            if (!canUseGsap) {
                return;
            }

            filter.classList.add('is-gsap');
            window.gsap.killTweensOf(indicator);

            if (!shouldAnimate || prefersReducedMotion) {
                window.gsap.set(indicator, {
                    x: targetX,
                    width: targetWidth,
                    scaleY: 1,
                    borderRadius: '50px'
                });
                return;
            }

            var currentX = Number(window.gsap.getProperty(indicator, 'x')) || 0;
            var distance = targetX - currentX;
            var direction = distance >= 0 ? 1 : -1;
            var stretch = Math.min(Math.max(Math.abs(distance) * 0.14, 14), 40);
            var stretchedX = currentX + distance * 0.46
                - (direction < 0 ? stretch : 0);
            var settlingX = targetX - (direction > 0 ? stretch * 0.18 : stretch * 0.3);
            var liquidShape = direction > 0
                ? '52% 100% 100% 52% / 50% 16% 84% 50%'
                : '100% 52% 52% 100% / 16% 50% 50% 84%';
            var settlingShape = direction > 0
                ? '50% 78% 78% 50% / 50% 30% 70% 50%'
                : '78% 50% 50% 78% / 30% 50% 50% 70%';
            var travelDuration = Math.min(0.32, 0.16 + Math.abs(distance) / 1400);

            window.gsap.timeline()
                .to(indicator, {
                    x: stretchedX,
                    width: targetWidth + stretch,
                    scaleY: 0.86,
                    borderRadius: liquidShape,
                    duration: travelDuration,
                    ease: 'power2.in'
                })
                .to(indicator, {
                    x: settlingX,
                    width: targetWidth + stretch * 0.45,
                    scaleY: 0.95,
                    borderRadius: settlingShape,
                    duration: 0.2,
                    ease: 'sine.inOut'
                })
                .to(indicator, {
                    x: targetX,
                    width: targetWidth,
                    scaleY: 1,
                    borderRadius: '50px',
                    duration: 0.42,
                    ease: 'elastic.out(1, 0.72)'
                });
        }

        function setActiveButton(value, shouldAnimate) {
            var activeButton = buttons.find(function (button) {
                return button.dataset.filterValue === value;
            }) || buttons[0];

            buttons.forEach(function (button) {
                var isActive = button === activeButton;

                button.classList.toggle('is-active', isActive);
                button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            });

            if (activeButton) {
                moveIndicator(activeButton, shouldAnimate);
            }

            updateDisabledButtons();
        }

        function getActiveValue() {
            var activeButton = filter.querySelector('.archive-filter__button.is-active');

            return activeButton ? activeButton.dataset.filterValue || '' : '';
        }

        function getFilterUrl(value) {
            var url = new URL(filter.action, window.location.href);

            if (value) {
                url.searchParams.set(parameter, value);
            } else {
                url.searchParams.delete(parameter);
            }

            url.searchParams.delete('paged');
            return url;
        }

        function updateDisabledButtons() {
            buttons.forEach(function (button) {
                button.disabled = isLoading || button.classList.contains('is-active');
            });
        }

        function setLoading(nextLoadingState) {
            isLoading = nextLoadingState;
            filter.classList.toggle('is-loading', isLoading);

            if (isLoading) {
                filter.setAttribute('aria-busy', 'true');
            } else {
                filter.removeAttribute('aria-busy');
            }

            updateDisabledButtons();
        }

        function loadResults(url, addHistory) {
            var currentRequest = requestNumber + 1;
            requestNumber = currentRequest;

            if (requestController) {
                requestController.abort();
            }

            requestController = typeof AbortController === 'function'
                ? new AbortController()
                : null;

            results.classList.add('is-loading');
            results.setAttribute('aria-busy', 'true');
            setLoading(true);

            window.fetch(url.toString(), {
                method: 'GET',
                credentials: 'same-origin',
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
                signal: requestController ? requestController.signal : undefined
            })
                .then(function (response) {
                    if (!response.ok) {
                        throw new Error('Request failed');
                    }

                    return response.text();
                })
                .then(function (html) {
                    var nextDocument = new DOMParser().parseFromString(html, 'text/html');
                    var nextResults = nextDocument.querySelector(resultsSelector);

                    if (!nextResults) {
                        throw new Error('Results not found');
                    }

                    results.innerHTML = nextResults.innerHTML;
                    document.title = nextDocument.title || document.title;

                    if (addHistory) {
                        window.history.pushState({}, '', url.toString());
                    }
                })
                .catch(function (error) {
                    if (error.name !== 'AbortError') {
                        window.location.assign(url.toString());
                    }
                })
                .finally(function () {
                    if (currentRequest !== requestNumber) {
                        return;
                    }

                    results.classList.remove('is-loading');
                    results.removeAttribute('aria-busy');
                    setLoading(false);
                });
        }

        function loadMoreResults(button) {
            var items = results.querySelector('[data-load-more-items]');
            var url = new URL(button.href, window.location.href);

            if (!items || isLoading) {
                return;
            }

            var currentRequest = requestNumber + 1;
            requestNumber = currentRequest;

            if (requestController) {
                requestController.abort();
            }

            requestController = typeof AbortController === 'function'
                ? new AbortController()
                : null;

            button.classList.add('is-loading');
            button.setAttribute('aria-disabled', 'true');
            results.setAttribute('aria-busy', 'true');
            setLoading(true);

            window.fetch(url.toString(), {
                method: 'GET',
                credentials: 'same-origin',
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
                signal: requestController ? requestController.signal : undefined
            })
                .then(function (response) {
                    if (!response.ok) {
                        throw new Error('Request failed');
                    }

                    return response.text();
                })
                .then(function (html) {
                    var nextDocument = new DOMParser().parseFromString(html, 'text/html');
                    var nextResults = nextDocument.querySelector(resultsSelector);
                    var nextItems = nextResults
                        ? nextResults.querySelector('[data-load-more-items]')
                        : null;

                    if (!nextResults || !nextItems) {
                        throw new Error('Results not found');
                    }

                    Array.prototype.slice.call(nextItems.children).forEach(function (item) {
                        items.appendChild(item);
                    });

                    var currentPagination = results.querySelector('.pagination');
                    var nextPagination = nextResults.querySelector('.pagination');
                    var currentControls = results.querySelector('[data-load-more-controls]');
                    var nextControls = nextResults.querySelector('[data-load-more-controls]');

                    if (currentPagination && nextPagination) {
                        currentPagination.replaceWith(nextPagination);
                    } else if (currentPagination) {
                        currentPagination.remove();
                    }

                    if (currentControls && nextControls) {
                        currentControls.replaceWith(nextControls);
                    } else if (currentControls) {
                        currentControls.remove();
                    }
                })
                .catch(function (error) {
                    if (error.name !== 'AbortError') {
                        window.location.assign(url.toString());
                    }
                })
                .finally(function () {
                    if (currentRequest !== requestNumber) {
                        return;
                    }

                    button.classList.remove('is-loading');
                    button.removeAttribute('aria-disabled');
                    results.removeAttribute('aria-busy');
                    setLoading(false);
                });
        }

        filter.addEventListener('submit', function (event) {
            var submitter = event.submitter || document.activeElement;

            if (!submitter || !submitter.matches('[data-filter-value]')) {
                return;
            }

            var value = submitter.dataset.filterValue || '';

            if (getActiveValue() === value) {
                event.preventDefault();
                return;
            }

            if (!results || typeof window.fetch !== 'function') {
                return;
            }

            event.preventDefault();
            setActiveButton(value, true);
            loadResults(getFilterUrl(value), true);
        });

        if (results && typeof window.fetch === 'function') {
            results.addEventListener('click', function (event) {
                var loadMoreButton = event.target.closest('[data-load-more]');
                var paginationLink = event.target.closest('.pagination a');

                if (loadMoreButton) {
                    event.preventDefault();
                    loadMoreResults(loadMoreButton);
                    return;
                }

                if (!paginationLink) {
                    return;
                }

                event.preventDefault();
                loadResults(new URL(paginationLink.href, window.location.href), true);
            });

            window.addEventListener('popstate', function () {
                var url = new URL(window.location.href);

                setActiveButton(url.searchParams.get(parameter) || '', true);
                loadResults(url, false);
            });
        }

        window.addEventListener('resize', function () {
            setActiveButton(getActiveValue(), false);
        });

        window.addEventListener('pageshow', function () {
            setLoading(false);
        });

        setActiveButton(
            new URL(window.location.href).searchParams.get(parameter) || '',
            false
        );
    }

    function init() {
        document.querySelectorAll('[data-archive-filter]').forEach(initArchiveFilter);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
}());
